<script>
  import { onMount } from "svelte";
  import { createForm } from "felte";
  import { validator } from "@felte/validator-zod";
  import { z } from "zod";
  import { getNotificationsContext } from "svelte-notifications";
  import { useQueryClient } from "@sveltestack/svelte-query"
  import SectionTitle from "../../../components/layout/SectionTitle.svelte";
  import Section from "../../../components/layout/Section.svelte";
  import SectionCaption from "../../../components/layout/SectionCaption.svelte";
  import ProjectSettingsTextInput from "../../../components/project/adminPage/parts/ProjectSettingsTextInput.svelte";
  import HelpTooltip from "../../../components/tooltip/HelpTooltip.svelte";
  import { translate } from "../../../i18n/i18next.js";
  import GeneralButton from "../../../components/button/GeneralButton.svelte";
  import { getAutoMpsProjectContext } from "../../AutoMpsProjectContext.js";
  import InputContainer from "../../parts/inputs/InputContainer.svelte";
  import Checkbox2 from "../../../components/input/Checkbox2.svelte";
  import Stack from "../../../components/layout/Stack.svelte";
  import Input from "../../parts/inputs/Input.svelte";

  const ctx = getAutoMpsProjectContext()

  const {addNotification} = getNotificationsContext()
  const queryClient = useQueryClient()

  const schema = z.object({
    executionEnginePath: z.string(),
    aruFilePath: z.string(),
    isDataRetentionEnabled: z.boolean(),
    dataRetention: z
      .number({
        invalid_type_error: "有効な数字ではありません。",
      })
      .int("整数を設定してください。")
      .min(1, "設定できる最小の値は1です。")
      .nullable(),
  })

  async function handleSubmit(values) {
    const response = await fetch(`/api/auto-mps/projects/${$ctx.projectId}/settings/asprova`, {
      method: "PUT",
      body: JSON.stringify({
        executionEnginePath: values.executionEnginePath,
        aruFilePath: values.aruFilePath,
        dataRetention: values.dataRetention,
      }),
      headers: {
        "Content-Type": "application/json",
      }
    })
    const responseData = await response.json().catch(() => null)
    if (response.ok) {
      addNotification({
        text: translate("frontend:generic.notifications.saved"),
        type: "success",
        removeAfter: 4000,
        position: "top-right",
      })
      await queryClient.invalidateQueries(`/api/auto-mps/projects/${$ctx.projectId}/settings/asprova`)
    } else {
      let message
      switch (responseData?.type) {
        case "error:auto-mps:settings:execution-engine:not-found":
          message = "指定されたパスに実行ファイルが見つかりません。"
          break
        case "error:auto-mps:settings:aru:not-found":
          message = "指定されたパスにAruファイルが見つかりません。"
          break
        default:
          message = translate("frontend:generic.notifications.saveFailed")
      }

      addNotification({
        text: message,
        type: "error",
        removeAfter: 4000,
        position: "top-right",
      })
    }
  }

  const {data, errors, form, setInitialValues, setFields, reset} = createForm({
    onSubmit: handleSubmit,
    initialValues: {
      executionEnginePath: "C:\\Program Files\\Asprova Corporation\\Asprova Auto MPS\\AsprovaAutoMPS.exe",
      aruFilePath: "C:\\AsprovaAutoMPS\\auto-mps.aru",
      isDataRetentionEnabled: false,
      dataRetention: null,
    },
    extend: validator({schema})
  })

  let status = "loading"
  async function loadAsprovaSettings() {
    try {
      const response = await fetch(`/api/auto-mps/projects/${$ctx.projectId}/settings/asprova`)
      if (!response.ok) {
        status = "error"
        return
      }
      const data = await response.json()
      setInitialValues({...data, isDataRetentionEnabled: data.dataRetention != null})
      reset()
      status = "loaded"
    } catch (e) {
      status = "error"
    }
  }

  function onClickDataRetentionCheckbox(e) {
    if (e.target.checked) {
      setFields("isDataRetentionEnabled", true)
      setFields("dataRetention", 30)
    } else {
      setFields("isDataRetentionEnabled", false)
      setFields("dataRetention", null)
    }
  }

  onMount(() => loadAsprovaSettings())
</script>

<Section>
  <SectionTitle>実行設定</SectionTitle>
  <SectionCaption>AsprovaAutoMPS.exe の場所、実行されるAruファイルの場所を指定します。</SectionCaption>
  <hr/>
  <form class="sub-section" use:form>
    <ProjectSettingsTextInput name="executionEnginePath" disabled={status !== "loaded"}>
      <span>実行エンジンのパス</span>
      <HelpTooltip title="実行エンジン">
        AsprovaAutoMPS.exe の場所を指定します。デフォルトの場所は
        C:\Program Files\Asprova Corporation\Asprova Auto MPS\AsprovaAutoMPS.exe です。
      </HelpTooltip>
    </ProjectSettingsTextInput>
    <ProjectSettingsTextInput name="aruFilePath" disabled={status !== "loaded"}>
      <span>Aruファイルのパス</span>
      <HelpTooltip title="実行エンジン">
        実行時に呼び出されるAruファイルのパスを指定します。
      </HelpTooltip>
    </ProjectSettingsTextInput>
    <InputContainer>
      <span slot="label">
        <span style="display: inline-flex; align-items: center; gap: .5rem">
          <Checkbox2 checked={$data.isDataRetentionEnabled} onClick={onClickDataRetentionCheckbox} />
            過去の実行結果を削除する
            <HelpTooltip title="過去の実行結果の削除">
              設定した日付を経過した過去の実行結果については削除します。削除を無効にした場合、すべての結果を保持し続けます。
            </HelpTooltip>
        </span>
      </span>
      <Stack direction="horizontal" alignItems={"center"}>
        <span class:disabled={status === "loading" || !$data.isDataRetentionEnabled}>
          過去 <Input name="dataRetention" style="text-align: right; width: 5rem" disabled={status === "loading" || $data.dataRetention == null} /> 日以前の結果を削除する
        </span>
      </Stack>
      <span class="error">
        {#if $data.isDataRetentionEnabled && $errors.dataRetention}
          {$errors.dataRetention}
        {/if}
      </span>
    </InputContainer>
    <div style="display: flex; justify-content: end">
      <GeneralButton type="submit">
        {translate("generic.save")}
      </GeneralButton>
    </div>
  </form>
</Section>

<style>
  hr {
    color: lightgrey;
  }

  .sub-section {
    display: flex;
    flex-direction: column;
    gap: .8rem;
    padding: .5rem 0
  }

  .disabled {
    color: darkgrey;
  }

  .error {
    color: red;
    font-size: 0.9rem;
  }
</style>
